import { format, parseISO } from 'date-fns';

/**
 * 
 * @param {string[]} userRoles The roles granted to the currently logged in user
 * @param {string[]} requiredRole List of roles required to access a resource (page). Any one will do.
 * @returns {boolean} true if at least one member of userRoles array is in requiredRole array.
 * 
 * That is if the user has one or more of the required roles.
 * Comparison is Case Insensitive.
 * 
 * @example
 * // returns true
 * isInRole(['Mahi.User', 'Mahi.Billing'], ['Mahi.User', 'Mahi.Admin'])
 * 
 * @example
 * // returns true
 * isInRole(['Mahi.User', 'Mahi.Billing'], ['Mahi.Admin', 'Mahi.BILLING'])
 * 
 * @example
 * // returns false
 * isInRole(['Mahi.User', 'Mahi.Billing'], ['Mahi.Admin', 'Mahi.AXOS'])
 *
 */
export function isInRole(userRoles, requiredRole)
{
  if (!userRoles || !(userRoles.length > 0)) {
    return false;
  }
  if (!requiredRole || !(requiredRole.length > 0)) {
    return false;
  }
  const lowerCaseUserRoles = userRoles.map(r => r.toLowerCase());
  const lowerCaseRequiredRole = requiredRole.map(r => r.toLowerCase());
  const userRolesIncludesSomeRequiredRole = lowerCaseRequiredRole.some(r => lowerCaseUserRoles.includes(r))
  return userRolesIncludesSomeRequiredRole;
}

/**
 * 
 * @param {string} isoDateString e.g. "2021-09-16T08:04:53.000000000+1200"
 * @param {*} outputFormat e.g. 'dd/MM/yyyy' might give '16/09/2021'.
 * @returns Your iso date string formatted according to the date-fns format string you provide.
 * Default format outputs e.g. "8:04am, 16 September 2021".
 */
export function dateFromISO(isoDateString, outputFormat = 'h:mmaaa, d MMMM y') {
    return format(parseISO(isoDateString), outputFormat);
}
/**
 * 
 * @param {number} someCounter 
 * @returns '' empty string if the parameter is 1, 's' otherwise. Use this to pluralise a noun.
 */
export function plural(someCounter) {
    return someCounter === 1 ? '' : 's';
}

export function notPlural(someCounter) {
    return someCounter !== 1 ? '' : 's';
}

export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function firstLetters(sentence, letterCount = null) {
  if (!sentence?.length) {
    return ''
  }
  const cleanSentence = sentence.replace(/\s/g, ' ').trim()
  const wordList = cleanSentence.split(' ')
  const resultLength = (letterCount === null ? wordList.length : letterCount)
  const firstLetterList = wordList.map((word) => word[0])
  const firstLetters = firstLetterList.join('')
  return firstLetters.substring(0, resultLength)
}

export function shortAddress(address, letterCount) {
  if (!address?.length) {
    return ''
  }
  const cleanAddress = address.replace(/\s/g, '').replace('[^a-zA-Z0-9]', '')
  return cleanAddress.substring(0, letterCount)
}

const inspectionEnabledStatuses = [
  'Scheduled',
  // 'Ready For Connection',
  'Inspections Team',
  'On Hold With Contractor',
]

export function isWorkOrderStatusEnabledForInspection(status) {
  return inspectionEnabledStatuses.includes(status)
}

export function getInspectionEnabledWorkOrderStatues() {
  return inspectionEnabledStatuses
}

export const safeFormatDate = (isoDateString, dateFormat='d MMMM yyyy') => {
    if (!isoDateString) {
        return ''
    }
    const dateObject = parseISO(isoDateString)
    if (!isValidDateObject(dateObject)) {
        return isoDateString
    }
    const formattedDateString = format(dateObject, dateFormat)
    return formattedDateString
}

export const isValidDateObject = (obj) => {
  return (obj && (obj instanceof Date) && !isNaN(obj.valueOf()))
}

export function toTitleCase(str) {
    return str.replace(
        /\w\S*/g,
        function(txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        }
    )
}